import { Navigate } from "react-router-dom";
import { usePermissions } from "./PermissionsContext";

// Route-level permission gate. Redirects away if the user does not have the
// required module access. Used to protect URL-typed navigation so a user who
// removes a tab cannot hit the module by typing its URL.
//
//   <RouteGate module="employee">
//     <Employee />
//   </RouteGate>
//
// Screen-level (optional):
//   <RouteGate module="profile" screen="org_details" action="view" redirectTo="/employee/home" />
export default function RouteGate({
  module: moduleKey,
  screen: screenKey,
  action = "view",
  children,
  redirectTo = "/home",
  /**
   * When set, users whose roleCode matches any entry (case-insensitive) are
   * allowed without evaluating module/screen checks. Used for the default
   * Employee bucket so legacy `/me/permissions` payloads that omit the new
   * `profile` module do not lock users out of `/employee/profile`.
   */
  bypassRoleCodes = [],
}) {
  const {
    hasModuleAccess,
    hasAnyModuleAccess,
    hasScreenAccess,
    loading,
    snapshot,
    isAdmin,
    roleCode,
  } = usePermissions();

  // While RBAC is still loading for the first time, don't redirect - just
  // render the children; the API call to /me/permissions resolves fast and we
  // don't want a flash of the home page on every refresh.
  if (loading && !snapshot) return children;

  if (isAdmin) return children;

  const roleUpper = String(roleCode || "").trim().toUpperCase();
  if (
    Array.isArray(bypassRoleCodes) &&
    bypassRoleCodes.some(
      (c) => String(c || "").trim().toUpperCase() === roleUpper
    )
  ) {
    return children;
  }

  let allowed;
  if (screenKey) {
    allowed = hasScreenAccess(moduleKey, screenKey, action);
  } else {
    // Allow when either the specific action is granted OR any access inside the
    // module exists (so a role with only "export" can still open the screen and
    // see the export button).
    allowed =
      hasModuleAccess(moduleKey, action) || hasAnyModuleAccess(moduleKey);
  }

  if (!allowed) return <Navigate to={redirectTo} replace />;
  return children;
}
